/**
 * RiskRulesAgGridHook
 *
 * AG Grid hook for the Risk Rule Engine page (Risk Rules menu item).
 *
 * Same layout as RiskDashboardAgGridHook: Rule pinned left, Actions pinned
 * right, Columns side panel for hide/show. Rows are the full @rules list for
 * the selected category — AG Grid's own pagination replaces the LiveView
 * page/per_page controls the plain table used to need.
 *
 * LiveView events pushed (via pushEvent):
 *  "toggle_rule" – { rule_id }  (enable/disable switch)
 *  "view_rule"   – { rule_id }  (opens the rule detail modal)
 *  "edit_rule"   – { rule_id }  (server-side push_patch to the edit form)
 *  "delete_rule" – { rule_id }  (deletes after a confirm prompt)
 */

// Tailwind classes (not inline hex) so these follow the app's `.theme-dark`
// overrides automatically instead of staying baked-in light-mode colors.
const typeBadge = (params) => {
  const value = params.value;
  if (!value) return "";
  const classes = value === "hold" ? "bg-red-50 text-red-700" : "bg-yellow-50 text-yellow-800";
  return `<span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}">${value.toUpperCase()}</span>`;
};

const ruleCell = (params) => {
  const { name, description } = params.data || {};
  return `<div class="py-1 leading-tight">
    <div class="text-sm font-medium text-gray-900">${name || ""}</div>
    <div class="max-w-[260px] truncate text-xs text-gray-500">${description || ""}</div>
  </div>`;
};

const RiskRulesAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");
    const hook = this;

    // The track/knob colors reference the app's shared CSS custom properties
    // directly (rgb(var(--c-*))) rather than literal hex, since a native
    // toggle like this isn't a great fit for static Tailwind classes but
    // still needs to flip with the theme — indigo-600 stays a fixed accent,
    // but the "off" track and the knob both need to invert.
    const toggleRenderer = (params) => {
      const data = params.data;
      if (!data) return "";
      const wrap = document.createElement("button");
      wrap.setAttribute("role", "switch");
      wrap.setAttribute("aria-checked", String(!!data.enabled));
      wrap.style.cssText = `position:relative;display:inline-flex;height:24px;width:44px;flex-shrink:0;
        cursor:pointer;border-radius:9999px;border:2px solid transparent;transition:background-color 0.2s ease-in-out;
        background:${data.enabled ? "#4F46E5" : "rgb(var(--c-gray-300))"};margin-top:8px;`;

      const knob = document.createElement("span");
      knob.style.cssText = `pointer-events:none;display:inline-block;height:20px;width:20px;border-radius:9999px;
        background:rgb(var(--c-surface));box-shadow:0 1px 2px rgba(0,0,0,0.2);transition:transform 0.2s ease-in-out;
        transform:translateX(${data.enabled ? "20px" : "0"});`;
      wrap.appendChild(knob);

      wrap.addEventListener("click", (e) => {
        e.stopPropagation();
        hook.pushEvent("toggle_rule", { rule_id: String(data.id) });
      });

      return wrap;
    };

    // Tailwind classes (not inline hex) so hover/focus and dark mode all
    // ride the same CSS this app already ships for its plain <button>s.
    const iconBtn = (svg, colourClasses, title, onClick) => {
      const btn = document.createElement("button");
      btn.title = title;
      btn.innerHTML = svg;
      btn.className = `inline-flex items-center justify-center w-7 h-7 mr-1 rounded-md border shadow-sm transition-colors duration-150 ${colourClasses}`;
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        onClick();
      });
      return btn;
    };

    const ICONS = {
      eye: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></svg>',
      edit: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"/></svg>',
      trash: '<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6h16Z"/></svg>',
    };

    const COLOURS = {
      blue: "text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300",
      indigo: "text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-200 hover:border-indigo-300",
      red: "text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border-red-200 hover:border-red-300",
    };

    const actionsRenderer = (params) => {
      const data = params.data;
      if (!data) return "";
      const wrap = document.createElement("div");
      wrap.style.cssText = "display:flex;align-items:center;justify-content:flex-end;padding:2px 0;";
      const id = String(data.id);

      wrap.appendChild(
        iconBtn(ICONS.eye, COLOURS.blue, "View", () => hook.pushEvent("view_rule", { rule_id: id }))
      );
      wrap.appendChild(
        iconBtn(ICONS.edit, COLOURS.indigo, "Edit", () => hook.pushEvent("edit_rule", { rule_id: id }))
      );
      wrap.appendChild(
        iconBtn(ICONS.trash, COLOURS.red, "Delete", () => {
          if (window.confirm("Are you sure you want to delete this rule?")) {
            hook.pushEvent("delete_rule", { rule_id: id });
          }
        })
      );

      return wrap;
    };

    const columnDefs = [
      {
        headerName: "Rule Name",
        field: "name",
        colId: "name",
        pinned: "left",
        width: 260,
        cellRenderer: ruleCell,
        filter: "agTextColumnFilter",
      },
      { headerName: "Type", field: "rule_type", flex: 0.9, minWidth: 100, cellRenderer: typeBadge },
      { headerName: "Category", field: "category", flex: 0.9, minWidth: 100 },
      { headerName: "Status", field: "enabled", flex: 0.7, minWidth: 90, cellRenderer: toggleRenderer, filter: false },
      { headerName: "Order", field: "execution_order", flex: 0.7, minWidth: 90, type: "numericColumn" },
      {
        headerName: "Actions",
        field: "id",
        colId: "actions",
        pinned: "right",
        width: 130,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: actionsRenderer,
      },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: false,
        minWidth: 80,
      },
      animateRows: true,
      rowHeight: 52,
      headerHeight: 40,
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50],
      popupParent: document.body,
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
        defaultToolPanel: "",
      },
      overlayNoRowsTemplate: `<span class="text-sm text-gray-500">No rules configured.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("RiskRulesAgGridHook: agGrid not found on window");
    }
  },

  updated() {
    if (this.gridApi && this.el.dataset.rows) {
      try {
        const rows = JSON.parse(this.el.dataset.rows);
        this.gridApi.setGridOption("rowData", rows);
      } catch (e) {
        console.warn("RiskRulesAgGridHook updated(): failed to parse rows", e);
      }
    }
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default RiskRulesAgGridHook;
